import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Avatar,
  Skeleton,
  Divider,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TodayIcon from '@mui/icons-material/Today';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DateRangeIcon from '@mui/icons-material/DateRange';
import UpcomingIcon from '@mui/icons-material/Upcoming';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import moment from 'moment';
import { getHousekeepingPanelPaperSx } from '../../utils/housekeepingMobileLayout';
import { getCheckInBookings, getBookingList } from '../../services/api';

const ACCENT = '#4ce1b6';
const PAGE_SIZE = 15;
const SECTION_KEYS = ['today', 'tomorrow', 'dayAfter', 'upcoming'];

const SECTION_META = {
  today: {
    label: 'Today',
    subtitle: 'Arrivals today',
    icon: TodayIcon,
    color: '#0d9488',
    dayOffset: 0,
  },
  tomorrow: {
    label: 'Tomorrow',
    subtitle: 'Arrivals tomorrow',
    icon: EventAvailableIcon,
    color: '#0284c7',
    dayOffset: 1,
  },
  dayAfter: {
    label: 'Day after tomorrow',
    subtitle: 'Arrivals in 2 days',
    icon: DateRangeIcon,
    color: '#ea580c',
    dayOffset: 2,
  },
  upcoming: {
    label: 'Upcoming',
    subtitle: 'Later arrivals',
    icon: UpcomingIcon,
    color: '#64748b',
    dayOffset: 3,
  },
};

function createEmptySection() {
  return {
    items: [],
    page: 0,
    total: 0,
    hasMore: true,
    loading: false,
    loadingMore: false,
  };
}

function uniqueJoin(values) {
  const seen = new Set();
  const out = [];
  values.forEach((v) => {
    const s = String(v || '').trim();
    if (!s || seen.has(s.toLowerCase())) return;
    seen.add(s.toLowerCase());
    out.push(s);
  });
  return out.join(', ');
}

function formatBoardDate(value) {
  if (!value || value === '—') return '';
  const m = moment(value, [moment.ISO_8601, 'YYYY-MM-DD', 'DD-MMM-YYYY', 'DD MMM YYYY'], true);
  if (m.isValid()) return m.format('DD MMM YYYY');
  const loose = moment(value);
  return loose.isValid() ? loose.format('DD MMM YYYY') : String(value);
}

function formatSectionHeadingDate(baseDate, dayOffset, sectionKey) {
  const base = moment(baseDate).isValid() ? moment(baseDate) : moment();
  if (sectionKey === 'upcoming') {
    return `From ${base.clone().add(dayOffset, 'day').format('DD MMM YYYY')}`;
  }
  return base.clone().add(dayOffset, 'day').format('DD MMM YYYY');
}

function getStatusTone(statusLabel, statusValue) {
  const raw = `${statusValue || ''} ${statusLabel || ''}`.toUpperCase().replace(/_/g, ' ');
  if (raw.includes('CHECKED IN') || raw.includes('IN HOUSE') || raw.includes('STAYING')) {
    return { color: '#0284c7', label: statusLabel || 'Checked In' };
  }
  if (raw.includes('CHECKED OUT') || raw.includes('CHECKOUT')) {
    return { color: '#64748b', label: statusLabel || 'Checked Out' };
  }
  if (raw.includes('NO SHOW')) {
    return { color: '#ea580c', label: statusLabel || 'No Show' };
  }
  if (raw.includes('RESERVED') || raw.includes('PENDING')) {
    return { color: '#ca8a04', label: statusLabel || 'Reserved' };
  }
  if (raw.includes('CONFIRMED')) {
    return { color: '#0d9488', label: statusLabel || 'Confirmed' };
  }
  const cleaned = String(statusLabel || statusValue || '—')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { color: '#64748b', label: cleaned };
}

export function mapHousekeepingArrivalBooking(booking) {
  if (!booking) return null;
  const statusValue = String(booking.status_value || '').toUpperCase();
  const statusLabel = String(booking.status || booking.status_value || '').trim();
  if (statusValue.includes('CANCEL') || statusLabel.toUpperCase().includes('CANCEL')) return null;

  const items = Array.isArray(booking.booking_items) ? booking.booking_items : [];
  const allocated = Array.isArray(booking.allocated_rooms) ? booking.allocated_rooms : [];

  const roomTypes = items.map((i) => i.room_type_name).filter(Boolean);
  const mealPlans = items
    .map((i) => i.rate_plan_name || i.rate_plan_code)
    .filter(Boolean);
  const roomNumbers = [
    ...allocated.map((r) => r.room_number),
    ...items.map((i) => i.allocated_room_number),
  ].filter(Boolean);

  const initials = String(booking.guest_name || 'G')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || 'G';

  const statusTone = getStatusTone(statusLabel, statusValue);

  return {
    id: booking.id,
    code: booking.code || `BK-${booking.id}`,
    guestName: booking.guest_name || 'Guest',
    roomType: uniqueJoin(roomTypes) || '—',
    mealPlan: uniqueJoin(mealPlans) || '—',
    roomNumber: uniqueJoin(roomNumbers) || 'Unassigned',
    checkInDate: formatBoardDate(booking.check_in_date),
    checkOutDate: formatBoardDate(booking.check_out_date),
    statusLabel: statusTone.label,
    statusColor: statusTone.color,
    initials,
  };
}

function extractBookingRows(data) {
  if (Array.isArray(data?.bookings)) return data.bookings;
  if (Array.isArray(data?.Data)) return data.Data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data)) return data;
  return [];
}

function mapPageRows(data) {
  return extractBookingRows(data).map(mapHousekeepingArrivalBooking).filter(Boolean);
}

const BookingRow = memo(function BookingRow({ booking, accent, isDark, index }) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        gap: 1.25,
        p: 1.25,
        borderRadius: 2,
        border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
        background: isDark
          ? `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha('#0f172a', 0.35)} 55%)`
          : `linear-gradient(135deg, ${alpha(accent, 0.1)} 0%, #ffffff 60%)`,
        boxShadow: isDark ? 'none' : `0 6px 18px ${alpha('#0f172a', 0.04)}`,
        overflow: 'hidden',
        animation: index < PAGE_SIZE ? `hkArrivalIn 360ms ease ${Math.min(index, 8) * 30}ms both` : 'none',
        transition: 'transform 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          borderColor: alpha(accent, 0.55),
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: `linear-gradient(180deg, ${accent}, ${alpha(accent, 0.35)})`,
        },
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          mt: 0.25,
          fontSize: '0.75rem',
          fontWeight: 800,
          bgcolor: alpha(accent, isDark ? 0.28 : 0.18),
          color: isDark ? '#e2e8f0' : accent,
          border: `1px solid ${alpha(accent, 0.45)}`,
        }}
      >
        {booking.initials}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.4 }}>
          <PersonOutlineIcon sx={{ fontSize: 14, color: accent, flexShrink: 0 }} />
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '0.82rem',
              color: isDark ? '#f8fafc' : '#0f172a',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            {booking.guestName}
          </Typography>
          <Chip
            size="small"
            label={booking.statusLabel}
            sx={{
              height: 18,
              maxWidth: 96,
              fontSize: '0.58rem',
              fontWeight: 800,
              flexShrink: 0,
              bgcolor: alpha(booking.statusColor, isDark ? 0.28 : 0.14),
              color: booking.statusColor,
              border: `1px solid ${alpha(booking.statusColor, 0.4)}`,
              '& .MuiChip-label': {
                px: 0.7,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        </Stack>

        <Stack spacing={0.3}>
          <Stack direction="row" spacing={0.6} alignItems="center">
            <BadgeIcon sx={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: isDark ? '#78d9c0' : '#0f766e' }}>
              {booking.code}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <LoginIcon sx={{ fontSize: 12, color: isDark ? '#81c784' : '#388e3c' }} />
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: isDark ? '#cbd5e1' : '#334155' }}>
              {booking.checkInDate || '—'}
            </Typography>
            <Box component="span" sx={{ opacity: 0.4, fontSize: '0.66rem' }}>→</Box>
            <LogoutIcon sx={{ fontSize: 12, color: isDark ? '#e57373' : '#d32f2f' }} />
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: isDark ? '#cbd5e1' : '#334155' }}>
              {booking.checkOutDate || '—'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.6} alignItems="flex-start">
            <MeetingRoomIcon sx={{ fontSize: 12, mt: '2px', color: isDark ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  color: isDark ? '#cbd5e1' : '#334155',
                  lineHeight: 1.35,
                  wordBreak: 'break-word',
                }}
              >
                {booking.roomType}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  color: isDark ? '#f8fafc' : '#0f172a',
                  lineHeight: 1.35,
                  wordBreak: 'break-word',
                }}
              >
                Rm {booking.roomNumber}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.6} alignItems="flex-start">
            <RestaurantIcon sx={{ fontSize: 12, mt: '2px', color: isDark ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
            <Typography
              sx={{
                fontSize: '0.68rem',
                color: isDark ? '#94a3b8' : '#64748b',
                lineHeight: 1.35,
                wordBreak: 'break-word',
              }}
            >
              Meal plan:{' '}
              <Box component="span" sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                {booking.mealPlan}
              </Box>
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
});

function TimelineSection({
  sectionKey,
  section,
  isDark,
  headingDate,
  scrollRootRef,
  onLoadMore,
}) {
  const meta = SECTION_META[sectionKey];
  const Icon = meta.icon;
  const sentinelRef = useRef(null);
  const count = section.total || section.items.length;
  const shown = section.items.length;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !section.hasMore || section.loading || section.loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore(sectionKey);
        }
      },
      {
        root: scrollRootRef?.current || null,
        rootMargin: '160px 0px',
        threshold: 0,
      },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    sectionKey,
    section.hasMore,
    section.loading,
    section.loadingMore,
    section.items.length,
    onLoadMore,
    scrollRootRef,
  ]);

  return (
    <Box
      sx={{
        mb: 1.75,
        borderRadius: 2.5,
        border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
        background: isDark ? alpha('#0f172a', 0.35) : alpha('#fff', 0.72),
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          background: isDark
            ? `linear-gradient(90deg, ${alpha(meta.color, 0.22)} 0%, transparent 100%)`
            : `linear-gradient(90deg, ${alpha(meta.color, 0.14)} 0%, transparent 100%)`,
          borderBottom: `1px solid ${alpha(meta.color, 0.22)}`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: alpha(meta.color, isDark ? 0.28 : 0.16),
              color: meta.color,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 17 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: isDark ? '#f1f5f9' : '#0f172a', lineHeight: 1.2 }}>
              {meta.label}
              {headingDate ? (
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    color: meta.color,
                  }}
                >
                  · {headingDate}
                </Box>
              ) : null}
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: isDark ? '#94a3b8' : '#64748b' }}>
              {meta.subtitle}
              {count > 0 ? ` · showing ${shown}/${count}` : ''}
            </Typography>
          </Box>
        </Stack>
        <Chip
          size="small"
          label={count}
          sx={{
            height: 22,
            fontWeight: 800,
            fontSize: '0.7rem',
            bgcolor: alpha(meta.color, isDark ? 0.28 : 0.14),
            color: isDark ? '#f8fafc' : meta.color,
            border: `1px solid ${alpha(meta.color, 0.35)}`,
          }}
        />
      </Box>

      <Stack spacing={1} sx={{ p: 1.25 }}>
        {section.loading && shown === 0 ? (
          [1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={96} sx={{ borderRadius: 2 }} />
          ))
        ) : shown === 0 ? (
          <Box
            sx={{
              py: 1.75,
              px: 1,
              textAlign: 'center',
              borderRadius: 2,
              border: `1px dashed ${isDark ? alpha('#fff', 0.12) : alpha('#0f172a', 0.1)}`,
              color: isDark ? '#64748b' : '#94a3b8',
            }}
          >
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>No bookings</Typography>
          </Box>
        ) : (
          section.items.map((booking, index) => (
            <BookingRow
              key={`${sectionKey}-${booking.id}-${booking.code}`}
              booking={booking}
              accent={meta.color}
              isDark={isDark}
              index={index}
            />
          ))
        )}

        {section.hasMore ? (
          <Box
            ref={sentinelRef}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: section.loadingMore ? 1 : 0.25,
              minHeight: 12,
            }}
          >
            {section.loadingMore ? (
              <CircularProgress size={18} sx={{ color: meta.color }} />
            ) : null}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}

/**
 * Right-rail arrival board for Housekeeping Room Status.
 * Loads 15 bookings per section, then another 15 when the section sentinel scrolls into view.
 */
export default function HousekeepingBookingTimeline({
  isDark,
  enabled = true,
  reloadKey = 0,
  referenceDate = null,
}) {
  const baseDate = useMemo(
    () => referenceDate || moment().format('YYYY-MM-DD'),
    [referenceDate],
  );

  const [sections, setSections] = useState(() => ({
    today: createEmptySection(),
    tomorrow: createEmptySection(),
    dayAfter: createEmptySection(),
    upcoming: createEmptySection(),
  }));

  const sectionsRef = useRef(sections);
  const loadingLockRef = useRef({});
  const scrollRootRef = useRef(null);

  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  const sectionDates = useMemo(
    () => ({
      today: formatSectionHeadingDate(baseDate, 0, 'today'),
      tomorrow: formatSectionHeadingDate(baseDate, 1, 'tomorrow'),
      dayAfter: formatSectionHeadingDate(baseDate, 2, 'dayAfter'),
      upcoming: formatSectionHeadingDate(baseDate, 3, 'upcoming'),
    }),
    [baseDate],
  );

  const dateBySection = useMemo(() => {
    const base = moment(baseDate);
    return {
      today: base.clone().format('YYYY-MM-DD'),
      tomorrow: base.clone().add(1, 'day').format('YYYY-MM-DD'),
      dayAfter: base.clone().add(2, 'day').format('YYYY-MM-DD'),
      upcomingFrom: base.clone().add(3, 'day').format('YYYY-MM-DD'),
    };
  }, [baseDate]);

  const fetchSectionPage = useCallback(
    async (sectionKey, page) => {
      if (sectionKey === 'upcoming') {
        const data = await getBookingList({
          check_in_from: dateBySection.upcomingFrom,
          per_page: PAGE_SIZE,
          page,
          sort: 'check_in_date',
        });
        const items = mapPageRows(data);
        const total = Number(data?.total_records ?? data?.count ?? items.length) || items.length;
        const hasMore =
          typeof data?.has_more === 'boolean'
            ? data.has_more
            : page * PAGE_SIZE < total && items.length > 0;
        return { items, total, hasMore };
      }

      const data = await getCheckInBookings(dateBySection[sectionKey], {
        page,
        per_page: PAGE_SIZE,
      });
      const items = mapPageRows(data);
      const total = Number(data?.count ?? items.length) || items.length;
      const hasMore =
        typeof data?.has_more === 'boolean'
          ? data.has_more
          : page * PAGE_SIZE < total && items.length > 0;
      return { items, total, hasMore };
    },
    [dateBySection],
  );

  const loadMore = useCallback(
    async (sectionKey) => {
      if (loadingLockRef.current[sectionKey]) return;

      const current = sectionsRef.current[sectionKey] || createEmptySection();
      if (!current.hasMore || current.loading || current.loadingMore) return;

      const pageToLoad = (current.page || 0) + 1;
      loadingLockRef.current[sectionKey] = true;

      setSections((prev) => ({
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          loading: pageToLoad === 1,
          loadingMore: pageToLoad > 1,
        },
      }));

      try {
        const { items, total, hasMore } = await fetchSectionPage(sectionKey, pageToLoad);
        setSections((prev) => {
          const prevSection = prev[sectionKey] || createEmptySection();
          const existingIds = new Set(prevSection.items.map((row) => row.id));
          const merged = [
            ...prevSection.items,
            ...items.filter((row) => !existingIds.has(row.id)),
          ];
          return {
            ...prev,
            [sectionKey]: {
              items: merged,
              page: pageToLoad,
              total,
              hasMore: Boolean(hasMore),
              loading: false,
              loadingMore: false,
            },
          };
        });
      } catch {
        setSections((prev) => ({
          ...prev,
          [sectionKey]: {
            ...(prev[sectionKey] || createEmptySection()),
            loading: false,
            loadingMore: false,
            hasMore: false,
          },
        }));
      } finally {
        loadingLockRef.current[sectionKey] = false;
      }
    },
    [fetchSectionPage],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    loadingLockRef.current = {};
    const empty = {
      today: createEmptySection(),
      tomorrow: createEmptySection(),
      dayAfter: createEmptySection(),
      upcoming: createEmptySection(),
    };
    sectionsRef.current = empty;
    setSections(empty);

    SECTION_KEYS.forEach((key) => {
      loadMore(key);
    });

    return undefined;
  }, [enabled, reloadKey, baseDate, loadMore]);

  const total = useMemo(
    () => SECTION_KEYS.reduce((sum, key) => sum + (sections[key]?.total || 0), 0),
    [sections],
  );

  return (
    <Box
      sx={{
        ...getHousekeepingPanelPaperSx(isDark, {
          p: 0,
          height: { md: 'calc(100vh - 210px)' },
          minHeight: { xs: 360, md: 520 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 3,
          position: { md: 'sticky' },
          top: { md: 16 },
        }),
        '@keyframes hkArrivalIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.5,
          borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
          background: isDark
            ? `linear-gradient(135deg, ${alpha(ACCENT, 0.16)} 0%, transparent 70%)`
            : `linear-gradient(135deg, ${alpha(ACCENT, 0.14)} 0%, transparent 70%)`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '0.95rem',
                color: isDark ? '#f8fafc' : '#0f172a',
                letterSpacing: 0.2,
              }}
            >
              Arrival board
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#64748b' }}>
              15 at a time · scroll for more
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${total} total`}
            sx={{
              fontWeight: 800,
              bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.16),
              color: isDark ? ACCENT : '#0f766e',
              border: `1px solid ${alpha(ACCENT, 0.4)}`,
            }}
          />
        </Stack>
      </Box>

      <Box
        ref={scrollRootRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.25,
          pt: 1.25,
          pb: 1.5,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(ACCENT, 0.35),
            borderRadius: 999,
          },
        }}
      >
        {SECTION_KEYS.map((key, idx) => (
          <React.Fragment key={key}>
            {idx === 3 ? (
              <Divider
                sx={{ my: 0.5, borderColor: isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.06) }}
              />
            ) : null}
            <TimelineSection
              sectionKey={key}
              section={sections[key]}
              isDark={isDark}
              headingDate={sectionDates[key]}
              scrollRootRef={scrollRootRef}
              onLoadMore={loadMore}
            />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
